import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { promptFor } from "@/data/prompts";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChatCircleDots } from "@phosphor-icons/react";

export function PromptDialog({ memory, open, onOpenChange, onSaved }) {
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);
  if (!memory) return null;

  const question = promptFor(memory.category);

  const save = async () => {
    setSaving(true);
    try {
      if (answer.trim()) {
        await api.put(`/events/${memory.id}`, {
          prompt_question: question,
          prompt_answer: answer.trim(),
        });
        toast.success("Your reflection is saved");
        onSaved?.();
      }
      onOpenChange(false);
      setAnswer("");
    } catch (e) {
      toast.error("Could not save your answer.");
    } finally {
      setSaving(false);
    }
  };

  const skip = () => { setAnswer(""); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="prompt-dialog" className="bg-[#FDFBF7] border-[#4A4540]/15 sm:max-w-md">
        <DialogTitle className="sr-only">A gentle question</DialogTitle>
        <div className="flex items-center gap-2 text-[#B08D57]">
          <ChatCircleDots size={22} weight="duotone" />
          <span className="text-sm font-medium uppercase tracking-wide">A gentle question</span>
        </div>
        <p className="font-serif text-2xl leading-snug text-[#2C2A28]">{question}</p>
        <p className="-mt-1 text-sm text-[#8A7F76]">Take your time. You can always skip.</p>
        <Textarea
          data-testid="prompt-answer-input"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Write a few words…"
          rows={4}
          className="bg-[#F4EFE6] border-[#4A4540]/20 focus-visible:ring-[#B08D57] text-[#2C2A28]"
        />
        <div className="flex items-center justify-between">
          <button data-testid="prompt-skip-btn" onClick={skip} className="text-sm text-[#8A7F76] hover:text-[#B08D57]">
            Skip for now
          </button>
          <Button
            data-testid="prompt-save-btn"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[#B08D57] px-6 text-[#FDFBF7] hover:-translate-y-0.5 hover:bg-[#C19A6B]"
          >
            {saving ? "Saving…" : "Save reflection"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
